import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
  Divider,
} from '@mui/material'
import { ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material'

export type CategoryFilterOption = {
  id: string
  name: string
  nameEn?: string
  order: number
  isActive: boolean
}

export type CategoryFilterDef = {
  enabled: boolean
  nameAr: string
  nameEn: string
  options: CategoryFilterOption[]
}

export type CategoryFiltersConfig = {
  filter1: CategoryFilterDef
  filter2: CategoryFilterDef
  filter3: CategoryFilterDef
}

export type FilterSlotKey = 'filter1' | 'filter2' | 'filter3'

export type ConditionPreset = 'new_used' | 'new_import'

const FILTER1_PRESETS: Record<
  ConditionPreset,
  { hint: string; options: Array<{ name: string; nameEn: string }> }
> = {
  new_used: {
    hint: 'مثل: جديد / مستعمل',
    options: [
      { name: 'جديد', nameEn: 'New' },
      { name: 'مستعمل', nameEn: 'Used' },
    ],
  },
  new_import: {
    hint: 'مثل: جديد / إستيراد',
    options: [
      { name: 'جديد', nameEn: 'New' },
      { name: 'إستيراد', nameEn: 'Import' },
    ],
  },
}

const FILTER_SLOTS: Array<{
  key: FilterSlotKey
  defaultNameAr: string
  defaultNameEn: string
  hint: string
}> = [
  {
    key: 'filter1',
    defaultNameAr: 'الحالة',
    defaultNameEn: 'Condition',
    hint: 'مثل: جديد / مستعمل',
  },
  {
    key: 'filter2',
    defaultNameAr: 'الماركة',
    defaultNameEn: 'Brand',
    hint: 'مثل: تويوتا / سامسونج',
  },
  {
    key: 'filter3',
    defaultNameAr: 'القسم',
    defaultNameEn: 'Section',
    hint: 'يُزامن مع الفئات الفرعية عند الحفظ',
  },
]

export function emptyFilterDef(): CategoryFilterDef {
  return { enabled: false, nameAr: '', nameEn: '', options: [] }
}

export function emptyCategoryFilters(): CategoryFiltersConfig {
  return {
    filter1: emptyFilterDef(),
    filter2: emptyFilterDef(),
    filter3: emptyFilterDef(),
  }
}

function parseOptions(raw: unknown): CategoryFilterOption[] {
  if (!Array.isArray(raw)) return []
  const list: CategoryFilterOption[] = []
  raw.forEach((item, index) => {
    if (!item || typeof item !== 'object') return
    const m = item as Record<string, unknown>
    const name = typeof m.name === 'string' ? m.name.trim() : ''
    if (!name) return
    list.push({
      id: typeof m.id === 'string' && m.id.trim() ? m.id.trim() : `opt_${index}_${Date.now()}`,
      name,
      nameEn: typeof m.nameEn === 'string' ? m.nameEn.trim() : '',
      order: typeof m.order === 'number' ? m.order : index,
      isActive: m.isActive !== false,
    })
  })
  list.sort((a, b) => (a.order !== b.order ? a.order - b.order : a.name.localeCompare(b.name)))
  return list
}

function parseFilterDef(raw: unknown): CategoryFilterDef {
  if (!raw || typeof raw !== 'object') return emptyFilterDef()
  const m = raw as Record<string, unknown>
  return {
    enabled: m.enabled === true,
    nameAr: typeof m.nameAr === 'string' ? m.nameAr : '',
    nameEn: typeof m.nameEn === 'string' ? m.nameEn : '',
    options: parseOptions(m.options),
  }
}

/** يقرأ categoryFilters من Firestore — بدون نسخ خيارات من فئات أخرى. */
export function parseCategoryFilters(raw: unknown): CategoryFiltersConfig {
  if (!raw || typeof raw !== 'object') return emptyCategoryFilters()
  const m = raw as Record<string, unknown>
  return {
    filter1: parseFilterDef(m.filter1),
    filter2: parseFilterDef(m.filter2),
    filter3: parseFilterDef(m.filter3),
  }
}

export function serializeCategoryFilters(config: CategoryFiltersConfig): CategoryFiltersConfig {
  const normalize = (def: CategoryFilterDef): CategoryFilterDef => ({
    enabled: def.enabled === true,
    nameAr: (def.nameAr ?? '').trim(),
    nameEn: (def.nameEn ?? '').trim(),
    options: (def.options ?? []).map((o, idx) => ({
      id: o.id?.trim() || `opt_${idx}_${Date.now()}`,
      name: o.name.trim(),
      nameEn: (o.nameEn ?? '').trim(),
      order: idx,
      isActive: o.isActive !== false,
    })),
  })
  return {
    filter1: normalize(config.filter1),
    filter2: normalize(config.filter2),
    filter3: normalize(config.filter3),
  }
}

/** خيارات filter3 → subSections بنفس الشكل. */
export function filter3OptionsToSubSections(
  options: CategoryFilterOption[],
): Array<{ id: string; name: string; nameEn: string; order: number; isActive: boolean }> {
  return options.map((o, idx) => ({
    id: o.id,
    name: o.name.trim(),
    nameEn: (o.nameEn ?? '').trim(),
    order: idx,
    isActive: o.isActive !== false,
  }))
}

/** subSections → خيارات filter3. */
export function subSectionsToFilter3Options(
  sections: Array<{ id: string; name: string; nameEn?: string; order: number; isActive: boolean }>,
): CategoryFilterOption[] {
  return sections.map((s, idx) => ({
    id: s.id,
    name: s.name.trim(),
    nameEn: (s.nameEn ?? '').trim(),
    order: idx,
    isActive: s.isActive !== false,
  }))
}

type Props = {
  value: CategoryFiltersConfig
  onSave: (config: CategoryFiltersConfig) => Promise<void>
  saving?: boolean
  disabled?: boolean
  /** خيارات الحالة الافتراضية عند تفعيل الفلتر 1 وهو فارغ */
  conditionPreset?: ConditionPreset
}

export default function CategoryFiltersEditor({
  value,
  onSave,
  saving = false,
  disabled = false,
  conditionPreset = 'new_used',
}: Props) {
  const [draft, setDraft] = useState<CategoryFiltersConfig>(() => serializeCategoryFilters(value))
  const [newOptionDrafts, setNewOptionDrafts] = useState<
    Record<FilterSlotKey, { name: string; nameEn: string }>
  >({
    filter1: { name: '', nameEn: '' },
    filter2: { name: '', nameEn: '' },
    filter3: { name: '', nameEn: '' },
  })

  useEffect(() => {
    setDraft(serializeCategoryFilters(value))
  }, [value])

  const updateFilter = (key: FilterSlotKey, patch: Partial<CategoryFilterDef>) => {
    setDraft(prev => {
      const next = { ...prev[key], ...patch }
      // عند تفعيل الفلتر 1 وهو بلا خيارات — املأ افتراضياً حسب الـ preset
      if (
        key === 'filter1' &&
        patch.enabled === true &&
        next.options.length === 0
      ) {
        const preset = FILTER1_PRESETS[conditionPreset]
        next.options = preset.options.map((o, idx) => ({
          id: `opt_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 7)}`,
          name: o.name,
          nameEn: o.nameEn,
          order: idx,
          isActive: true,
        }))
        if (!next.nameAr.trim()) next.nameAr = 'الحالة'
        if (!next.nameEn.trim()) next.nameEn = 'Condition'
      }
      return { ...prev, [key]: next }
    })
  }

  const updateOption = (
    key: FilterSlotKey,
    optionId: string,
    patch: Partial<Pick<CategoryFilterOption, 'name' | 'nameEn' | 'isActive'>>,
  ) => {
    setDraft(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        options: prev[key].options.map(o =>
          o.id === optionId ? { ...o, ...patch } : o,
        ),
      },
    }))
  }

  const moveOption = (key: FilterSlotKey, index: number, direction: 'up' | 'down') => {
    setDraft(prev => {
      const options = [...prev[key].options]
      const target = direction === 'up' ? index - 1 : index + 1
      if (target < 0 || target >= options.length) return prev
      const temp = options[index]
      options[index] = options[target]
      options[target] = temp
      return {
        ...prev,
        [key]: {
          ...prev[key],
          options: options.map((o, idx) => ({ ...o, order: idx })),
        },
      }
    })
  }

  const deleteOption = (key: FilterSlotKey, optionId: string) => {
    setDraft(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        options: prev[key].options
          .filter(o => o.id !== optionId)
          .map((o, idx) => ({ ...o, order: idx })),
      },
    }))
  }

  const addOption = (key: FilterSlotKey) => {
    const draftOpt = newOptionDrafts[key]
    const name = draftOpt.name.trim()
    if (!name) return
    const next: CategoryFilterOption = {
      id: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name,
      nameEn: draftOpt.nameEn.trim(),
      order: draft[key].options.length,
      isActive: true,
    }
    setDraft(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        options: [...prev[key].options, next],
      },
    }))
    setNewOptionDrafts(prev => ({
      ...prev,
      [key]: { name: '', nameEn: '' },
    }))
  }

  const handleSave = async () => {
    const normalized = serializeCategoryFilters(draft)
    // إسقاط خيارات بلا اسم عربي
    const cleaned: CategoryFiltersConfig = {
      filter1: {
        ...normalized.filter1,
        options: normalized.filter1.options.filter(o => o.name.trim()),
      },
      filter2: {
        ...normalized.filter2,
        options: normalized.filter2.options.filter(o => o.name.trim()),
      },
      filter3: {
        ...normalized.filter3,
        options: normalized.filter3.options.filter(o => o.name.trim()),
      },
    }
    await onSave(cleaned)
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        حتى 3 فلاتر اختيارية لهذه الفئة. اترك الفلتر معطلاً إن لم تحتاجه. لا تُنسخ خيارات من فئات أخرى
        تلقائياً.
      </Typography>

      <Stack spacing={2} divider={<Divider flexItem />}>
        {FILTER_SLOTS.map(slot => {
          const filter = draft[slot.key]
          const titleAr = filter.nameAr.trim() || slot.defaultNameAr
          const titleEn = filter.nameEn.trim() || slot.defaultNameEn
          return (
            <Box key={slot.key}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mb: 1 }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={filter.enabled}
                      disabled={disabled || saving}
                      onChange={e => updateFilter(slot.key, { enabled: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={`${titleAr} / ${titleEn}`}
                  sx={{ ml: 0, minWidth: 160 }}
                />
                <TextField
                  size="small"
                  label="الاسم عربي"
                  placeholder={slot.defaultNameAr}
                  value={filter.nameAr}
                  disabled={disabled || saving}
                  onChange={e => updateFilter(slot.key, { nameAr: e.target.value })}
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <TextField
                  size="small"
                  label="Name (EN)"
                  placeholder={slot.defaultNameEn}
                  value={filter.nameEn}
                  disabled={disabled || saving}
                  onChange={e => updateFilter(slot.key, { nameEn: e.target.value })}
                  sx={{ flex: 1, minWidth: 120 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {slot.key === 'filter1' ? FILTER1_PRESETS[conditionPreset].hint : slot.hint}
                {!filter.enabled ? ' — معطل: لن يظهر للمشتري حتى مع وجود خيارات.' : ''}
              </Typography>

              {filter.options.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  لا توجد خيارات{filter.enabled ? '' : ' (مسموح والفلتر معطل)'}.
                </Typography>
              ) : (
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {filter.options.map((opt, optIndex) => (
                    <Stack
                      key={opt.id}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      sx={{
                        opacity: opt.isActive ? 1 : 0.75,
                        p: 0.5,
                        borderRadius: 1,
                        bgcolor: opt.isActive ? 'transparent' : 'action.hover',
                      }}
                    >
                      <Typography sx={{ minWidth: 24 }}>{optIndex + 1}.</Typography>
                      <TextField
                        size="small"
                        label="عربي"
                        value={opt.name}
                        disabled={disabled || saving}
                        onChange={e =>
                          updateOption(slot.key, opt.id, { name: e.target.value })
                        }
                        sx={{ flex: 1, minWidth: 120 }}
                      />
                      <TextField
                        size="small"
                        label="English"
                        value={opt.nameEn ?? ''}
                        disabled={disabled || saving}
                        onChange={e =>
                          updateOption(slot.key, opt.id, { nameEn: e.target.value })
                        }
                        sx={{ flex: 1, minWidth: 120 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={opt.isActive}
                            disabled={disabled || saving}
                            onChange={() =>
                              updateOption(slot.key, opt.id, { isActive: !opt.isActive })
                            }
                            color="primary"
                          />
                        }
                        label={opt.isActive ? 'نشط' : 'غير نشط'}
                        sx={{ ml: 0, mr: 0 }}
                      />
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          size="small"
                          disabled={disabled || saving || optIndex === 0}
                          onClick={() => moveOption(slot.key, optIndex, 'up')}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={
                            disabled || saving || optIndex === filter.options.length - 1
                          }
                          onClick={() => moveOption(slot.key, optIndex, 'down')}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={disabled || saving}
                          onClick={() => deleteOption(slot.key, opt.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  label="خيار جديد (عربي)"
                  value={newOptionDrafts[slot.key].name}
                  disabled={disabled || saving}
                  onChange={e =>
                    setNewOptionDrafts(prev => ({
                      ...prev,
                      [slot.key]: { ...prev[slot.key], name: e.target.value },
                    }))
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="English"
                  value={newOptionDrafts[slot.key].nameEn}
                  disabled={disabled || saving}
                  onChange={e =>
                    setNewOptionDrafts(prev => ({
                      ...prev,
                      [slot.key]: { ...prev[slot.key], nameEn: e.target.value },
                    }))
                  }
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={disabled || saving || !newOptionDrafts[slot.key].name.trim()}
                  onClick={() => addOption(slot.key)}
                >
                  إضافة خيار
                </Button>
              </Stack>
            </Box>
          )
        })}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          disabled={disabled || saving}
          onClick={() => void handleSave()}
        >
          {saving ? 'جاري الحفظ…' : 'حفظ فلاتر الفئة'}
        </Button>
      </Box>
    </Box>
  )
}
